<template>
  <aside class="app-sidebar">
    <div class="brand" @click="goHome">
      <img class="brand-icon" :src="logo" alt="" />
      <span class="brand-name" :title="companyName">{{ companyName }}</span>
    </div>

    <nav class="sidebar-nav">
      <router-link
        v-for="item in navItems"
        :key="item.path"
        :to="item.path"
        class="nav-item"
        :class="{
          active: isActive(item.path),
          'nav-item--remind': menuBadge(item.path) > 0
        }"
      >
        <img
          class="nav-icon"
          :src="isActive(item.path) ? item.iconActive : item.icon"
          alt=""
        />
        <span class="nav-text">{{ item.title }}</span>
        <em
          v-if="menuBadge(item.path)"
          class="nav-badge"
          :title="menuBadgeTitle(item.path)"
        >{{ formatBadge(menuBadge(item.path)) }}</em>
      </router-link>
    </nav>

    <div class="sidebar-footer">
      <button
        type="button"
        class="logout-btn"
        :disabled="loggingOut"
        @click="handleLogout"
      >
        <svg
          class="logout-btn__icon"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M6.75 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V3.75C2.25 3.35218 2.40804 2.97064 2.68934 2.68934C2.97064 2.40804 3.35218 2.25 3.75 2.25H6.75"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12 12.75L15.75 9L12 5.25"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M15.75 9H6.75"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="logout-btn__text">{{ loggingOut ? '退出中…' : '退出登录' }}</span>
      </button>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'AppFooter'
}
</script>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { logout as logoutApi } from '@/api/memberAuth'
import { getUserInfo as getStoredUserInfo, saveLoginSession, clearLoginSession } from '@/utils/auth'
import { getUserInfo as fetchUserInfo } from '@/api/memberUser'
import { useAuditMenuBadge } from '@/composables/useAuditMenuBadge'

import logo from '@/assets/logo.png'
import iconOrders from '@/assets/comimages/dingdanguanli.png'
import iconOrdersActive from '@/assets/comimages/dingdanguanlied.png'
import iconDrivers from '@/assets/comimages/sijiguanli.png'
import iconDriversActive from '@/assets/comimages/sijiguanlied.png'
import iconMonitor from '@/assets/comimages/dingdanjiankong.png'
import iconMonitorActive from '@/assets/comimages/dingdanjiankonged.png'
import iconRejectReview from '@/assets/profileimages/judanshenhe.png'
import iconFinance from '@/assets/comimages/caiwutongji.png'
import iconFinanceActive from '@/assets/comimages/caiwutongjied.png'
import iconProfile from '@/assets/comimages/gerenzhongxin.png'
import iconProfileActive from '@/assets/comimages/gerenzhongxined.png'
import iconVehicles from '@/assets/comimages/cheliangguanli.png'
import iconVehiclesActive from '@/assets/comimages/cheliangguanlied.png'

const router = useRouter()
const route = useRoute()
const loggingOut = ref(false)
const companyName = ref('调度公司')
const { rejectPendingCount, orderPendingCount, stopAuditRemind } = useAuditMenuBadge()

function applyCompanyName(userInfo) {
  companyName.value = userInfo?.dispatchBindCompanyName || '调度公司'
}

async function loadCompanyName() {
  applyCompanyName(getStoredUserInfo())
  try {
    const data = await fetchUserInfo()
    applyCompanyName(data)
    if (data) {
      saveLoginSession(null, data)
    }
  } catch (err) {
    console.error('加载调度公司名称失败:', err)
  }
}

onMounted(() => {
  loadCompanyName()
})

const navItems = [
  { path: '/orders', title: '订单管理', icon: iconOrders, iconActive: iconOrdersActive },
  { path: '/drivers', title: '司机管理', icon: iconDrivers, iconActive: iconDriversActive },
  { path: '/vehicles', title: '车辆管理', icon: iconVehicles, iconActive: iconVehiclesActive },
  { path: '/service-scope', title: '运营范围', icon: iconMonitor, iconActive: iconMonitorActive },
  { path: '/monitor', title: '订单监控', icon: iconMonitor, iconActive: iconMonitorActive },
  { path: '/reject-review', title: '拒单审核', icon: iconRejectReview, iconActive: iconRejectReview },
  { path: '/contract-review', title: '合同信息', icon: iconRejectReview, iconActive: iconRejectReview },
  { path: '/finance', title: '财务统计', icon: iconFinance, iconActive: iconFinanceActive },
  { path: '/profile', title: '个人中心', icon: iconProfile, iconActive: iconProfileActive },
]

function isActive(path) {
  return route.path === path || route.path.startsWith(`${path}/`)
}

function menuBadge(path) {
  if (path === '/orders') return orderPendingCount.value
  if (path === '/reject-review') return rejectPendingCount.value
  return 0
}

function menuBadgeTitle(path) {
  const n = menuBadge(path)
  if (path === '/orders') return `${n} 条待接订单`
  if (path === '/reject-review') return `${n} 条拒单待审核`
  return ''
}

function formatBadge(n) {
  if (!n || n <= 0) return ''
  return n > 99 ? '99+' : String(n)
}

function goHome() {
  router.push('/orders')
}

async function handleLogout() {
  if (loggingOut.value) return
  loggingOut.value = true
  try {
    await logoutApi()
  } catch {
    /* 接口失败仍清除本地登录态 */
  } finally {
    stopAuditRemind()
    clearLoginSession()
    router.push('/login')
    loggingOut.value = false
  }
}
</script>

<style scoped>
.app-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 1001;
  width: var(--sidebar-width);
  background: var(--bg-surface);
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  height: var(--header-height);
  padding: 0 16px 0 20px;
  cursor: pointer;
  flex-shrink: 0;
  min-width: 0;
  border-bottom: 1px solid var(--border-color);
}

.brand-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: contain;
  flex-shrink: 0;
}

.brand-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.sidebar-nav {
  flex: 1;
  padding: 12px 0;
  overflow-y: auto;
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  margin: 2px 10px;
  padding: 0 12px 0 14px;
  border-radius: 8px;
  text-decoration: none;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  transition: color 160ms var(--ease-out), background 160ms var(--ease-out);
}

.nav-item:hover {
  color: var(--color-primary);
  background: var(--color-primary-soft);
}

.nav-item.active {
  color: var(--color-primary);
  background: rgba(5, 123, 250, 0.12);
}

.nav-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 10px;
  bottom: 10px;
  width: 3px;
  border-radius: 0 2px 2px 0;
  background: var(--color-primary);
}

.nav-item:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 1px;
}

.nav-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  object-fit: contain;
}

.nav-text {
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.nav-item--remind {
  position: relative;
  animation: nav-item-breathe 2s ease-in-out infinite;
}

.nav-item--remind::after {
  content: '';
  position: absolute;
  inset: 4px 8px;
  border-radius: 8px;
  border: 1.5px solid rgba(245, 108, 108, 0.55);
  pointer-events: none;
  animation: nav-item-ring 2s ease-in-out infinite;
}

.nav-item.active.nav-item--remind::after {
  border-color: rgba(5, 123, 250, 0.45);
}

.nav-badge {
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--color-danger);
  color: #fff;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}

.nav-item.active .nav-badge {
  background: #e64545;
}

@keyframes nav-item-breathe {
  0%,
  100% {
    background: transparent;
  }
  50% {
    background: rgba(245, 108, 108, 0.08);
  }
}

.nav-item.active.nav-item--remind {
  animation-name: nav-item-breathe-active;
}

@keyframes nav-item-breathe-active {
  0%,
  100% {
    background: #f5f9ff;
  }
  50% {
    background: rgba(5, 123, 250, 0.12);
  }
}

@keyframes nav-item-ring {
  0%,
  100% {
    opacity: 0.9;
    transform: scale(1);
  }
  50% {
    opacity: 0.2;
    transform: scale(1.02);
  }
}

.sidebar-footer {
  flex-shrink: 0;
  align-self: stretch;
  border-top: 1px solid var(--border-color);
  padding: 16px 12px 20px;
}

.logout-btn {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  gap: 8px;
  border: none;
  border-radius: 8px;
  background: var(--color-primary);
  cursor: pointer;
  transition: background 160ms var(--ease-out), transform 150ms var(--ease-out);
}

.logout-btn:hover:not(:disabled) {
  background: var(--color-primary-hover);
}

.logout-btn:active:not(:disabled) {
  background: #0358b8;
}

.logout-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.logout-btn__icon {
  flex: none;
  width: 17.3px;
  height: 18px;
  color: #fff;
}

.logout-btn__text {
  flex: none;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: #fff;
  white-space: nowrap;
}
</style>
